import { provide, ref } from 'vue'
import usePflegeUtils from './usePflegeUtils.js'
import { Tracking, type EtData, type TrackingDataLayer, type TrackingEvent } from '@webcarelbj/shared-components'
import type { PageTrackingData, VideoPlayData, VideoViewData } from '~/types/tracking'

const { toSystemFriendlyString } = usePflegeUtils()
let useTrackingClassInstance: Tracking | null = null
const etData: Ref<EtData | null> = ref(null)

export default function useTracking() {
  const init = (window: Window, document: Document, gtmKey: string, initData: TrackingDataLayer = {}) => {
    if (window && document && gtmKey) {
      if (!useTrackingClassInstance) {
        useTrackingClassInstance = new Tracking()
      }
      useTrackingClassInstance.initGTM(window, document, gtmKey)
      provide('track', (trackingEvent: TrackingEvent, trackingLabel: string) => {
        useTrackingClassInstance?.trackDatalayer({
          event: trackingEvent,
          label: trackingLabel,
        })
      })

      useTrackingClassInstance.addData(initData)
    }
  }

  const initET = async (productName: string, versionNumber: string|null = null) => {
    if (!useTrackingClassInstance) {
      useTrackingClassInstance = new Tracking()
    }
    await useTrackingClassInstance.initET(
      productName,
      versionNumber,
      usePageConfig().etUrl,
    )
    etData.value = useTrackingClassInstance?.etData
  }

  const trackDatalayer = (data: TrackingDataLayer) => {
    if (useTrackingClassInstance) {
      useTrackingClassInstance?.trackDatalayer(data)
    }
  }

  const addData = (data: string) => {
    if (useTrackingClassInstance) {
      useTrackingClassInstance.addData(data)
    }
  }

  const trackClick = (component: string, mainComponent: string | undefined, label: string, additionalData: PageTrackingData | null = null) => {
    const eventData = {
      event: 'gaEvent',
      event_name: 'click',
      click: {
        component,
        main_component: mainComponent,
        label,
      },
    }

    if (additionalData?.value !== null) {
      Object.assign(eventData.click, additionalData?.value)
    }
    provide('track', () => {
      useTrackingClassInstance?.trackDatalayer(eventData)
    })
  }

  const trackVideo = (data: VideoPlayData) => {
    const eventData = {
      event: 'gaEvent',
      event_name: 'video_interaction',
      video_interaction: data,
    }
    useTrackingClassInstance?.trackDatalayer(eventData)
  }

  const trackView = (data: VideoViewData) => {
    const eventData = {
      event: 'gaEvent',
      event_name: 'view',
      view: data,
    }
    useTrackingClassInstance?.trackDatalayer(eventData)
  }

  const createTrackingLabel = (label:  string | undefined) => {
    return toSystemFriendlyString(label)
  }

  return {
    init,
    initET,
    trackDatalayer,
    addData,
    trackClick,
    createTrackingLabel,
    etData,
    trackVideo,
    trackView,
  }
}
